import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { Check, ChevronDown, PartyPopper } from "lucide-react";
import TimePicker from "../common/TimePicker";
import OutcomeSelect from "../common/OutcomeSelect";
import DateTimeSelect from "../common/DateTimeSelect";

function defaultSignUpLaterValue() {
  const pad = (n) => String(n).padStart(2, "0");
  const d = new Date();
  d.setHours(d.getHours() + 1, 0, 0, 0);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function defaultFollowUpLaterValue() {
  const pad = (n) => String(n).padStart(2, "0");
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(d.getHours() + 1, 0, 0, 0);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function ProfileSelector({ profiles, value, onChange }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selected = profiles.find((l) => String(l.id) === String(value));

  return (
    <Box sx={{ position: "relative" }} ref={containerRef}>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen(!open)}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: "12px",
          py: "10px",
          borderRadius: "calc(0.75rem - 2px)",
          border: "1px solid hsl(140 8% 88%)",
          backgroundColor: "hsla(140, 8%, 90%, 0.3)",
          cursor: "pointer",
          fontSize: "14px",
          fontFamily: "Segoe UI Regular, system-ui, sans-serif",
          boxSizing: "border-box",
          transition: "background-color 0.15s",
          "&:hover": {
            backgroundColor: "hsl(140 8% 90%)",
          },
        }}
      >
        <Typography component="span" sx={{ fontWeight: 500, fontSize: 14, color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
          {selected ? `${selected.first_name || ""} ${selected.last_name || ""}`.trim() || "Unnamed" : "Select a profile…"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {selected?.pipeline_name && (
            <Typography component="span" sx={{ fontSize: 12, color: "hsl(150 10% 46%)" }}>
              {selected.pipeline_name}
            </Typography>
          )}
          <ChevronDown size={14} style={{ color: "hsl(150, 10%, 46%)" }} />
        </Box>
      </Box>
      {open && (
        <Paper
          elevation={0}
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            mt: "4px",
            borderRadius: "0.75rem",
            border: "1px solid hsl(140 8% 88%)",
            boxShadow: "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
            zIndex: 1000,
            py: "4px",
          }}
        >
          {profiles.map((profile) => (
            <Box
              key={profile.id}
              component="button"
              type="button"
              onClick={() => {
                onChange(profile.id);
                setOpen(false);
              }}
              sx={{
                width: "100%",
                textAlign: "left",
                px: "16px",
                py: "10px",
                fontSize: "14px",
                fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
                border: "none",
                backgroundColor: "transparent",
                transition: "background-color 0.15s",
                "&:hover": {
                  backgroundColor: "hsla(140, 8%, 90%, 0.5)",
                },
              }}
            >
              <Typography component="span" sx={{ fontSize: 14, color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                {`${profile.first_name || ""} ${profile.last_name || ""}`.trim() || "Unnamed"}
              </Typography>
              {profile.pipeline_name && (
                <Typography component="span" sx={{ fontSize: 12, color: "hsl(150 10% 46%)" }}>
                  {profile.pipeline_name}
                </Typography>
              )}
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
}

const LogCallCard = ({ profiles, onLogCall }) => {
  const [selectedProfileId, setSelectedProfileId] = useState(profiles[0]?.id ?? null);
  const [selectedOutcome, setSelectedOutcome] = useState(null);
  const [callbackTime, setCallbackTime] = useState("");
  const [callbackDateTime, setCallbackDateTime] = useState("");
  const [callNotes, setCallNotes] = useState("");

  const needsTime = selectedOutcome === "follow_up_today";
  const needsDateTime = selectedOutcome === "sign_up_later" || selectedOutcome === "follow_up_later";

  // Keep selectedProfileId in sync if the profiles list changes
  useEffect(() => {
    if (profiles.length > 0 && !profiles.some((l) => String(l.id) === String(selectedProfileId))) {
      setSelectedProfileId(profiles[0].id);
    }
  }, [profiles, selectedProfileId]);

  const handleSave = () => {
    if (!selectedProfileId || !selectedOutcome) return;
    const followUpAt = needsDateTime && callbackDateTime
      ? new Date(callbackDateTime).toISOString()
      : undefined;
    onLogCall(selectedProfileId, selectedOutcome, callbackTime, callNotes, followUpAt);
    setSelectedOutcome(null);
    setCallbackTime("");
    setCallbackDateTime("");
    setCallNotes("");
  };

  if (!profiles || profiles.length === 0) {
    return (
      <Paper elevation={0} sx={{ borderRadius: "0.75rem", border: "1px solid hsl(140 8% 88%)", backgroundColor: "#ffffff" }}>
        <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid hsl(140 8% 88%)" }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>Log call</Typography>
        </Box>
        <Box sx={{ p: 4, textAlign: "center" }}>
          <PartyPopper size={32} style={{ color: "hsl(150 38% 17%)", margin: "0 auto 12px" }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>
            All calls done!
          </Typography>
          <Typography sx={{ fontSize: 13, color: "hsl(150 10% 46%)", mt: 0.5 }}>
            Great work today 🎉
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ borderRadius: "0.75rem", border: "1px solid hsl(140 8% 88%)", backgroundColor: "#ffffff" }}>
      <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid hsl(140 8% 88%)" }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Semibold, sans-serif, system-ui" }}>Log call</Typography>
      </Box>

      <Stack spacing={2} sx={{ p: 2.5 }}>
        <ProfileSelector
          profiles={profiles}
          value={selectedProfileId}
          onChange={setSelectedProfileId}
        />

        <OutcomeSelect
          value={selectedOutcome}
          onChange={(val) => {
            setSelectedOutcome(val);
            if (val !== "follow_up_today") setCallbackTime("");
            if (val === "sign_up_later") {
              setCallbackDateTime((prev) => prev || defaultSignUpLaterValue());
            } else if (val === "follow_up_later") {
              setCallbackDateTime((prev) => prev || defaultFollowUpLaterValue());
            } else {
              setCallbackDateTime("");
            }
          }}
        />

        {needsTime && (
          <TimePicker value={callbackTime} onChange={setCallbackTime} />
        )}

        {needsDateTime && (
          <DateTimeSelect value={callbackDateTime} onChange={setCallbackDateTime} />
        )}

        {selectedOutcome && (
          <Box
            component="textarea"
            ref={(el) => {
              if (el) {
                el.style.height = "auto";
                el.style.height = `${el.scrollHeight}px`;
              }
            }}
            value={callNotes}
            onChange={(e) => {
              setCallNotes(e.target.value);
              const el = e.target;
              el.style.height = "auto";
              el.style.height = `${el.scrollHeight}px`;
            }}
            placeholder="Add a note about this call..."
            rows={3}
            sx={{
              display: "flex",
              width: "100%",
              minHeight: "unset",
              borderRadius: "calc(0.75rem - 2px)",
              border: "1px solid transparent",
              backgroundColor: "hsla(140, 8%, 90%, 0.3)",
              p: "8px",
              fontSize: "14px",
              color: "hsl(150 30% 12%)",
              resize: "none",
              outline: "none",
              overflow: "hidden",
              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              boxSizing: "border-box",
              "&::placeholder": { color: "hsl(150 10% 46%)", opacity: 1 },
              "&:focus": { outline: "none", boxShadow: "none", borderColor: "hsl(140 8% 82%)" },
            }}
          />
        )}

        <Button
          disableRipple
          disableElevation
          disabled={!selectedOutcome}
          onClick={handleSave}
          sx={{
            width: "100%",
            fontSize: "14px",
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            px: "16px",
            py: "10px",
            lineHeight: "20px",
            minHeight: 0,
            height: "auto",
            minWidth: 0,
            borderRadius: "0.75rem",
            backgroundColor: "hsl(150 38% 17%)",
            color: "hsl(80 97% 72%)",
            transition: "opacity 0.2s",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            textTransform: "none",
            "&:hover": {
              opacity: 0.9,
              backgroundColor: "hsl(150 38% 17%)",
            },
            "&.Mui-disabled": {
              opacity: 0.4,
              cursor: "not-allowed",
              pointerEvents: "auto",
              color: "hsl(80 97% 72%)",
              backgroundColor: "hsl(150 38% 17%)",
            },
          }}
        >
          <Check size={16} />
          Save outcome
        </Button>
      </Stack>
    </Paper>
  );
};

export default LogCallCard;
